import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { formatearMoneda, type EstadoFactura, type FiltroVencimiento } from "@uirtus/shared";
import { EstadoDominio } from "../componentes/estado-dominio";
import { api } from "../lib/api";
import { useResumenDominios } from "../lib/dominios";

interface FacturaResumen {
  id: string;
  estado: EstadoFactura;
  total: string;
  moneda: string;
}

export function Tablero() {
  const { data } = useQuery({
    queryKey: ["facturas", "resumen"],
    queryFn: () => api<FacturaResumen[]>("/facturas?limite=200"),
  });

  const { data: dominios } = useResumenDominios();
  const facturas = data ?? [];
  const timbradas = facturas.filter((f) => f.estado === "TIMBRADA");
  const pendientes = facturas.filter((f) => f.estado === "PENDIENTE_TIMBRE");
  const conError = facturas.filter((f) => f.estado === "ERROR_TIMBRE");
  const facturado = timbradas.reduce((acc, f) => acc + Number(f.total), 0);

  return (
    <div className="space-y-6">
      <h1 className="text-lg font-semibold">Tablero</h1>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tarjeta titulo="Facturado" valor={formatearMoneda(facturado)} />
        <Tarjeta titulo="Timbradas" valor={String(timbradas.length)} />
        <Tarjeta
          titulo="En proceso"
          valor={String(pendientes.length)}
          alerta={pendientes.length > 0}
        />
        <Tarjeta
          titulo="Con error"
          valor={String(conError.length)}
          alerta={conError.length > 0}
        />
      </div>

      {pendientes.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          Hay {pendientes.length} factura(s) esperando resolución del PAC. El
          proceso de reconciliación las revisa cada dos minutos — no hace falta
          hacer nada.{" "}
          <Link to="/facturas" className="underline">
            Ver facturas
          </Link>
        </div>
      )}

      {dominios && (
        <section className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">Dominios</h2>
            <Link to="/dominios" className="text-sm text-marca-600 hover:underline">
              Ver todos
            </Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <TarjetaEnlace titulo="Administrados" valor={dominios.total} a="/dominios" />
            <TarjetaEnlace
              titulo="Vencidos"
              valor={dominios.vencidos}
              tono="peligro"
              a="/dominios"
              search={{ vencimiento: "vencidos" }}
            />
            <TarjetaEnlace
              titulo="Vencen en 30 días"
              valor={dominios.vencenEn30}
              tono="alerta"
              a="/dominios"
              search={{ vencimiento: "por_vencer", dias: 30 }}
            />
            <TarjetaEnlace
              titulo="Vencen en 60 días"
              valor={dominios.vencenEn60}
              tono="alerta"
              a="/dominios"
              search={{ vencimiento: "por_vencer", dias: 60 }}
            />
          </div>

          {dominios.urgentes.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
              <h3 className="border-b border-slate-200 px-4 py-3 text-sm font-semibold dark:border-slate-800">
                Próximos a renovar
              </h3>
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {dominios.urgentes.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                    <div className="min-w-0">
                      <Link
                        to="/dominios/$id"
                        params={{ id: d.id }}
                        className="font-mono font-medium text-marca-600 hover:underline dark:text-marca-500"
                      >
                        {d.nombre}
                      </Link>
                      <div className="truncate text-xs text-slate-500">{d.cliente.razonSocial}</div>
                    </div>
                    <EstadoDominio dias={d.diasRestantes} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Tarjeta({
  titulo,
  valor,
  alerta,
}: {
  titulo: string;
  valor: string;
  alerta?: boolean;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-xs uppercase text-slate-500">{titulo}</p>
      <p
        className={`mt-1 text-2xl font-semibold ${alerta ? "text-amber-600" : ""}`}
      >
        {valor}
      </p>
    </div>
  );
}

/** Indicador que lleva a la lista ya filtrada. El color solo se enciende si hay algo que atender. */
function TarjetaEnlace({
  titulo,
  valor,
  tono,
  a,
  search,
}: {
  titulo: string;
  valor: number;
  tono?: "alerta" | "peligro";
  a: "/dominios";
  search?: { vencimiento: FiltroVencimiento; dias?: number };
}) {
  const color =
    valor > 0 && tono === "peligro"
      ? "text-red-600"
      : valor > 0 && tono === "alerta"
        ? "text-amber-600"
        : "";
  return (
    <Link
      to={a}
      search={search ?? {}}
      className="block rounded-lg border border-slate-200 bg-white p-4 transition hover:border-marca-500 dark:border-slate-800 dark:bg-slate-900"
    >
      <p className="text-xs uppercase text-slate-500">{titulo}</p>
      <p className={`mt-1 text-2xl font-semibold ${color}`}>{valor}</p>
    </Link>
  );
}
